import axios from 'axios';

export type AppErrorCode =
  | 'UNAUTHORIZED' // 401
  | 'FORBIDDEN' // 403
  | 'NOT_FOUND' // 404
  | 'VALIDATION_ERROR' // 422
  | 'SERVER_ERROR' // 500+
  | 'NETWORK_ERROR' // Axios network issues
  | 'UNKNOWN_ERROR';

export interface AppError {
  code: AppErrorCode;
  message: string;
  originalError?: unknown;
  status?: number;
}

export function isAppError(error: unknown): error is AppError {
  if (typeof error !== 'object' || error === null) {
    return false;
  }

  return 'code' in error && 'message' in error;
}

/**
 * Normalizes diverse transport errors (Axios, Network, Runtime) into a stable AppError structure.
 */
export function normalizeAppError(error: unknown): AppError {
  // --- Axios Handling ---
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;

    if (!status) {
      return {
        code: 'NETWORK_ERROR',
        message: 'Network error. Please check your connection.',
        originalError: error
      };
    }

    if (status === 401) {
      return {
        code: 'UNAUTHORIZED',
        message: 'Your session has expired. Please log in again.',
        status,
        originalError: error
      };
    }

    if (status === 403) {
      return {
        code: 'FORBIDDEN',
        message: "You don't have permission to perform this action.",
        status,
        originalError: error
      };
    }

    if (status === 404) {
      return {
        code: 'NOT_FOUND',
        message: 'The requested resource was not found.',
        status,
        originalError: error
      };
    }

    if (status === 400 || status === 422) {
      return {
        code: 'VALIDATION_ERROR',
        message: error.response?.data?.message || 'The provided data was invalid.',
        status,
        originalError: error
      };
    }

    if (status >= 500) {
      return {
        code: 'SERVER_ERROR',
        message: 'Our servers are having trouble. Please try again later.',
        status,
        originalError: error
      };
    }
  }

  // --- Runtime / Generic Handling ---
  const message = error instanceof Error ? error.message : 'An unexpected error occurred.';
  return {
    code: 'UNKNOWN_ERROR',
    message,
    originalError: error
  };
}
